import { fmt, fmtQty, parseQty, type Product } from '@k2pos/core';
import { useEffect, useRef, useState } from 'react';
import { Button } from './Button';
import { UnitChip } from './Chip';
import { Mono, Text } from './Text';
import { IconBackspace } from '@/components/icons';
import { colors } from '@/theme/tokens';
import styles from './QtySheet.module.css';

/** `back` deletes a character; `.` starts the fractional part for bulk units. */
const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '.', '0', 'back'];

interface QtySheetProps {
  product: Product | null;
  unitIndex: number;
  /** Quantity already in the cart for the selected unit. */
  qty: number;
  onSelectUnit: (index: number) => void;
  onConfirm: (qty: number) => void;
  onClose: () => void;
}

/**
 * Quantity entry for one product. Typing "20" beats tapping `+` twenty times,
 * which is the whole point — a 20-bag cement sale is a normal counter sale.
 *
 * Built on `<dialog>` rather than a hand-rolled overlay: it is supported at the
 * Chrome 90 floor and brings a focus trap, Esc-to-close, inertness of the page
 * behind it and `::backdrop` for free — all things the RN Modal it replaces had
 * to be given manually or went without.
 */
export function QtySheet({
  product,
  unitIndex,
  qty,
  onSelectUnit,
  onConfirm,
  onClose,
}: QtySheetProps) {
  const ref = useRef<HTMLDialogElement>(null);
  // Held as a string so a half-typed "1." survives until the user finishes.
  const [draft, setDraft] = useState('');

  // Re-seed whenever the sheet opens on a product, or the unit switches under it.
  useEffect(() => {
    if (product) setDraft(qty > 0 ? fmtQty(qty) : '');
  }, [product, unitIndex, qty]);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (product && !dialog.open) dialog.showModal();
    if (!product && dialog.open) dialog.close();
  }, [product]);

  if (!product) return null;

  const unit = product.units[unitIndex] ?? product.units[0]!;
  // parseQty, not Number(): it normalises the comma that Lao and Thai keyboards
  // put on the decimal key, so "0,5" is half a ton rather than NaN.
  const value = parseQty(draft);
  const valid = value !== null && value > 0;
  const lineTotal = valid ? Math.round(unit.price * value) : 0;

  function press(key: string) {
    setDraft((current) => {
      if (key === 'back') return current.slice(0, -1);
      if (key === '.') return current.includes('.') ? current : `${current || '0'}.`;
      // Avoid "007" while still allowing "0.5".
      if (current === '0') return key;
      const next = `${current}${key}`;
      return next.length > 7 ? current : next;
    });
  }

  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      onClose={onClose}
      // Clicking the backdrop closes; clicks inside the sheet stop here.
      onClick={(e) => {
        if (e.target === ref.current) onClose();
      }}
    >
      <div className={styles.sheet}>
        <div className={styles.grabber} />

        <Text weight={800} size={16} lines={2}>
          {product.name}
        </Text>
        <Mono size={11.5} color={colors.textMuted}>
          {fmt(unit.price)} ₭ / {unit.unit}
        </Mono>

        {product.units.length > 1 ? (
          <div className={styles.units} role="radiogroup" aria-label="ໜ່ວຍ">
            {product.units.map((u, i) => (
              <UnitChip
                key={u.unit}
                unit={u.unit}
                selected={i === unitIndex}
                onPress={() => onSelectUnit(i)}
              />
            ))}
          </div>
        ) : null}

        <div className={styles.display}>
          <Mono weight={700} size={34} color={valid ? colors.text : colors.textFaint}>
            {draft || '0'}
          </Mono>
          <Text size={13} color={colors.textMuted}>
            {unit.unit}
          </Text>
          <div className="grow" />
          <Mono weight={700} size={17} color={colors.skyDeep}>
            {fmt(lineTotal)} ₭
          </Mono>
        </div>

        <div className={styles.keypad}>
          {KEYS.map((key) => (
            <button
              key={key}
              type="button"
              aria-label={key === 'back' ? 'ລຶບ' : key}
              onClick={() => press(key)}
              className={styles.key}
            >
              {key === 'back' ? (
                <IconBackspace size={22} />
              ) : (
                <Mono weight={700} size={20} color={colors.text}>
                  {key}
                </Mono>
              )}
            </button>
          ))}
        </div>

        <div className={styles.actions}>
          {qty > 0 ? (
            <Button
              label="ເອົາອອກ"
              variant="outline"
              size={14}
              className="grow"
              onPress={() => onConfirm(0)}
            />
          ) : null}
          <Button
            label="ຕົກລົງ"
            size={15}
            className="grow"
            disabled={!valid}
            onPress={() => valid && onConfirm(value)}
          />
        </div>
      </div>
    </dialog>
  );
}
